import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";

import { useDeleteRoleAssignmentMutation } from "state/rtkQuery/access/accessMutations";
import { extractApiErrorMessage } from "utils/accessManagement/extractApiErrorMessage";
import { getAssigneeDisplay, resolveAssignmentRole } from "utils/accessManagement/roleAssignmentDisplay";
import { showToast } from "utils/reusableToast";

// Deletion is deliberate and permanent — this dialog surfaces the identifying
// details of the record about to be removed and requires an explicit confirm click.
const DeleteAssignmentDialog = ({ assignment, rolesById, onClose, onDeleted }) => {
  const [deleteAssignment, { isLoading, isError }] = useDeleteRoleAssignmentMutation();
  const [localError, setLocalError] = useState(null);

  if (!assignment) return null;

  const { primary } = getAssigneeDisplay(assignment);
  const { label: roleLabel } = resolveAssignmentRole(assignment, rolesById);

  const handleClose = () => {
    setLocalError(null);
    onClose();
  };

  const handleDelete = async () => {
    setLocalError(null);
    try {
      await deleteAssignment(assignment.id).unwrap();
      showToast({ title: "Assignment deleted", description: `The role assignment for ${primary} was permanently removed.`, type: "success" });
      onDeleted?.(assignment);
      handleClose();
    } catch (err) {
      setLocalError(extractApiErrorMessage(err, "Failed to delete the role assignment. Please try again."));
    }
  };

  return (
    <Dialog
      open={!!assignment}
      onClose={handleClose}
      // Opened from a button inside the group-assignments Dialog — without an
      // explicit z-index above the shared default modal layer, this can
      // render behind that already-open Dialog instead of on top of it.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
    >
      <DialogTitle>Delete role assignment?</DialogTitle>
      <DialogContent sx={{ maxWidth: 440 }}>
        <DialogContentText component="div">
          <p style={{ marginTop: 0 }}>This permanently removes the assignment record. This cannot be undone.</p>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px", background: "#f9fafb" }}>
            <div style={{ fontSize: 13 }}><strong>Assignee:</strong> {primary}</div>
            <div style={{ fontSize: 13 }}><strong>Role:</strong> {roleLabel}</div>
            <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "monospace", marginTop: 4 }}>ID: {assignment.id}</div>
          </div>
          {(localError || isError) && (
            <Alert severity="error" sx={{ mt: 2, fontSize: 13 }}>{localError}</Alert>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={(event) => {
            // This dialog is stacked on top of the group-assignments Dialog
            // (see the zIndex note above). Without stopping propagation here,
            // React's delegated event system can let this same click bubble
            // out to the OUTER Dialog's backdrop-click-away detection after
            // this dialog unmounts mid-click — closing the group modal
            // behind it too, not just this confirmation.
            event.stopPropagation();
            handleDelete();
          }}
          disabled={isLoading}
          color="error"
          variant="contained"
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DeleteAssignmentDialog.propTypes = {
  assignment: PropTypes.object,
  rolesById: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func,
};

export default DeleteAssignmentDialog;
